import type { Directive } from 'vue'

/**
 * Diretiva v-numeric para inputs de texto que aceitam apenas numeros.
 *
 * Uso:
 *   v-numeric          → aceita apenas digitos (inteiro)
 *   v-numeric.decimal  → aceita digitos, virgula e ponto (decimal brasileiro)
 *
 * Comportamento:
 *   - Bloqueia keypress de caracteres nao numericos
 *   - No blur, normaliza virgula para ponto e dispara evento input
 *   - Compativel com v-model.number (apos normalizacao, parseFloat funciona)
 *   - No decimal, permite apenas um separador (. ou ,)
 */
export const vNumeric: Directive = {
  mounted(el: HTMLElement, binding) {
    const input = el.tagName === 'INPUT' ? el as HTMLInputElement : el.querySelector('input') as HTMLInputElement
    if (!input) return

    const isDecimal = binding.modifiers.decimal

    // Keypress: bloqueia caracteres invalidos
    const handleKeypress = (e: KeyboardEvent) => {
      // Permitir teclas de controle
      if (e.ctrlKey || e.metaKey || e.altKey) return
      if (['Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight', 'Enter', 'Home', 'End'].includes(e.key)) return

      if (isDecimal) {
        // Decimal: aceita digitos, ponto, virgula e sinal negativo
        if (/[\d]/.test(e.key)) return
        if ((e.key === '.' || e.key === ',') && !input.value.includes('.') && !input.value.includes(',')) return
        if (e.key === '-' && input.selectionStart === 0 && !input.value.includes('-')) return
        e.preventDefault()
      } else {
        // Inteiro: aceita apenas digitos e sinal negativo
        if (/\d/.test(e.key)) return
        if (e.key === '-' && input.selectionStart === 0 && !input.value.includes('-')) return
        e.preventDefault()
      }
    }

    // Paste: filtra caracteres invalidos
    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault()
      const text = e.clipboardData?.getData('text') || ''
      let filtered: string
      if (isDecimal) {
        filtered = text.replace(/[^\d.,-]/g, '')
      } else {
        filtered = text.replace(/[^\d-]/g, '')
      }
      document.execCommand('insertText', false, filtered)
    }

    // Blur: normaliza virgula para ponto e dispara input event
    const handleBlur = () => {
      if (isDecimal && input.value.includes(',')) {
        input.value = input.value.replace(',', '.')
        input.dispatchEvent(new Event('input', { bubbles: true }))
      }
    }

    input.addEventListener('keypress', handleKeypress)
    input.addEventListener('paste', handlePaste)
    input.addEventListener('blur', handleBlur)

    // Salvar refs para cleanup
    ;(input as any).__vNumericCleanup = () => {
      input.removeEventListener('keypress', handleKeypress)
      input.removeEventListener('paste', handlePaste)
      input.removeEventListener('blur', handleBlur)
    }
  },

  unmounted(el: HTMLElement) {
    const input = el.tagName === 'INPUT' ? el as HTMLInputElement : el.querySelector('input') as HTMLInputElement
    if (input && (input as any).__vNumericCleanup) {
      (input as any).__vNumericCleanup()
    }
  }
}
